































































































import { mapGetters } from 'vuex';
import { getCampaignsListSearch } from '@/api/CopyStake/GamificationCenter/WheelOfFortune';
import { errorToastMessage } from '@/helpers/errorToastMessage';
import { truncateString } from '@/helpers/formatString';
import { DataTableHeader } from 'vuetify';
import {
  EWheelOfFortuneCampaignActivationType,
  EWheelOfFortuneCampaignSearchBy,
  EWheelOfFortuneCampaignStatusType,
  IWheelOfFortuneCampaign,
  IWheelOfFortuneCampaignListParams,
  ValidationRules
} from '@/api/schema';
import * as vr from '@/helpers/validation';

const CopyStakeWheelOfFortuneCampaignsTableFiltersDialog = (): Promise<any> =>
  import(
    '@/views/CopyStake/CopyStakeGamificationCenter/WheelOfFortune/CampaignsTable/CopyStakeWheelOfFortuneCampaignsTableFiltersDialog.vue'
  );
const VStatus = (): Promise<any> => import('@/components/VStatus.vue');

type ISelectItem = {
  text: string;
  value: EWheelOfFortuneCampaignSearchBy;
};

const mapActivatedTypeName = {
  [EWheelOfFortuneCampaignActivationType.BY_OPERATOR]: 'Operator',
  [EWheelOfFortuneCampaignActivationType.BY_STREAMER]: 'Streamer'
};

const statusMap = {
  [EWheelOfFortuneCampaignStatusType.ACTIVE]: {
    label: 'Running',
    type: 'system-success'
  },
  [EWheelOfFortuneCampaignStatusType.DELETED]: {
    label: 'Deleted',
    type: 'additional-deleted'
  },
  [EWheelOfFortuneCampaignStatusType.FINISHED]: {
    label: 'Finished',
    type: 'additional-finished'
  },
  [EWheelOfFortuneCampaignStatusType.PENDING]: {
    label: 'Pending',
    type: 'additional-pending'
  }
};

const DEFAULT_PAGINATION_OPTIONS = {
  page: 1,
  itemsPerPage: 10,
  sortBy: ['createdAt'],
  sortDesc: [false]
} as const;

const DEFAULT_SEARCH_OPTIONS = {
  searchBy: EWheelOfFortuneCampaignSearchBy.NAME,
  search: ''
} as const;

export default {
  name: 'CopyStakeGamificationCenterWheelOfFortuneCampaignsTable',
  components: {
    CopyStakeWheelOfFortuneCampaignsTableFiltersDialog,
    VStatus
  },
  props: {
    needToRefetch: {
      type: Boolean,
      default: false
    }
  },
  data(): Record<string, unknown> {
    return {
      EWheelOfFortuneCampaignStatusType,
      mapActivatedTypeName,
      statusMap,
      isWheelOfFortuneCampaignsLoading: false,
      wheelOfFortuneCampaigns: [],
      totalWheelOfFortuneCampaigns: null,
      tableOptions: { ...DEFAULT_PAGINATION_OPTIONS },
      tmpSearchOptions: { ...DEFAULT_SEARCH_OPTIONS },
      searchOptions: { ...DEFAULT_SEARCH_OPTIONS },
      filtersOptions: null,
      dialogs: {
        campaignsFilters: false
      }
    };
  },
  computed: {
    ...mapGetters('Onboarding', ['operatorId']),
    searchByOptions(): ISelectItem[] {
      return [
        {
          text: 'Name',
          value: EWheelOfFortuneCampaignSearchBy.NAME
        },
        {
          text: 'Campaign ID',
          value: EWheelOfFortuneCampaignSearchBy.CAMPAIGN_ID
        }
      ];
    },
    currentSearchBy(): ISelectItem | null {
      return (
        this.searchByOptions.find(
          (option: ISelectItem) =>
            option.value === this.tmpSearchOptions.searchBy
        ) ?? null
      );
    },
    searchPlaceholder(): string {
      if (!this.currentSearchBy) {
        return '';
      }

      return `Search by ${this.currentSearchBy.text}`;
    },
    headers(): DataTableHeader[] {
      return [
        { text: 'ID', value: 'id' },
        { text: 'Name', value: 'name' },
        { text: 'Activated by', value: 'activationType' },
        { text: 'Creation', value: 'createdAt' },
        { text: 'Start (UTC)', value: 'startAt' },
        { text: 'End (UTC)', value: 'finishAt' },
        { text: 'Status', value: 'status' },
        { text: 'Action', value: 'actions', sortable: false }
      ];
    },
    requestParams(): IWheelOfFortuneCampaignListParams {
      return {
        ...(this.searchOptions.search
          ? { [this.searchOptions.searchBy]: this.searchOptions.search }
          : {}),
        ...(this.filtersOptions ? this.filtersOptions : {}),
        size: this.tableOptions.itemsPerPage,
        page: this.tableOptions.page - 1,
        sort: this.tableOptions.sortBy[0],
        order: this.tableOptions.sortDesc[0] ? 'DESC' : 'ASC',
        operatorId: this.operatorId
      };
    },
    rules(): ValidationRules {
      return {
        name: [(v) => (v ? vr.minLength(v, 3) : true)],
        wheelId: [
          (v) => (v ? vr.validNumber(v) : true),
          (v) => (v ? vr.positiveNumber(v) : true)
        ]
      };
    },
    getSearchRules(): ValidationRules {
      return this.rules[this.currentSearchBy.value];
    }
  },
  watch: {
    needToRefetch: {
      async handler(value: boolean): Promise<void> {
        if (value) {
          await this.fetchWheelOfFortuneCampaigns();
        }
      }
    },
    requestParams: {
      async handler(): Promise<void> {
        await this.fetchWheelOfFortuneCampaigns();
      },
      deep: true
    }
  },
  methods: {
    truncateString,
    async fetchWheelOfFortuneCampaigns(): Promise<void> {
      try {
        this.isWheelOfFortuneCampaignsLoading = true;

        const {
          content: wheelOfFortuneCampaigns,
          totalElements: totalWheelOfFortuneCampaigns
        } = await getCampaignsListSearch(this.requestParams);

        this.wheelOfFortuneCampaigns = wheelOfFortuneCampaigns;
        this.totalWheelOfFortuneCampaigns = totalWheelOfFortuneCampaigns;

        this.$emit('update:needToRefetch', false);
      } catch (error) {
        errorToastMessage(error);
      } finally {
        this.isWheelOfFortuneCampaignsLoading = false;
      }
    },

    updateRequestOptions(newOptions: Record<string, unknown>): void {
      const searchInput = this.$refs.searchInput;
      searchInput.validate();

      this.$nextTick((): void => {
        if (searchInput.valid) {
          this.searchOptions.searchBy =
            newOptions.searchBy ?? DEFAULT_SEARCH_OPTIONS.searchBy;
          this.searchOptions.search =
            newOptions.search ?? DEFAULT_SEARCH_OPTIONS.search;

          this.tableOptions.page = DEFAULT_PAGINATION_OPTIONS.page;
          this.tableOptions.itemsPerPage =
            DEFAULT_PAGINATION_OPTIONS.itemsPerPage;
        }
      });
    },

    handleClickClearSearchInput(): void {
      this.updateRequestOptions({
        ...this.tmpSearchOptions,
        search: ''
      });
    },
    handleKeydownEnterSearchInput(): void {
      this.updateRequestOptions(this.tmpSearchOptions);
    },
    handleClickSearchButton(): void {
      this.updateRequestOptions(this.tmpSearchOptions);
    },
    handleClickFiltersButton(): void {
      this.openCampaignTableFiltersDialog();
    },
    openCampaignTableFiltersDialog(): void {
      this.dialogs.campaignsFilters = true;
    },
    handleClickEditCampaignButton(campaign: IWheelOfFortuneCampaign): void {
      this.$emit('edit-campaign', campaign);
    },
    handleClickActivateCampaignStatus(
      campaign: IWheelOfFortuneCampaign
    ): void {
      this.$emit('activate-campaign', campaign);
    },
    handleClickDeactivateCampaignStatus(
      campaign: IWheelOfFortuneCampaign
    ): void {
      this.$emit('deactivate-campaign', campaign);
    },
    handleClickCopyCampaignBtn(campaign: IWheelOfFortuneCampaign): void {
      this.$emit('copy-campaign', campaign);
    }
  }
};
